import React from "react";
import { Helmet } from "react-helmet-async";
import { Target, Eye, ShieldCheck, Zap, Users } from "lucide-react";
import { aboutImage, statsData } from "../data/mock";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import CTASection from "../components/CTASection";
import { useLanguage } from "../i18n/LanguageContext";
import { usePublicContent } from "../i18n/PublicContentContext";

const valueIcons = [ShieldCheck, Zap, Users];
const resolveImg = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function About() {
  const { t, lang } = useLanguage();
  const pub = usePublicContent();
  const a = t.about;

  const ov = pub.about || {};
  const L = (en, hi, def) => {
    const v = lang === "hi" ? (ov?.[hi] || ov?.[en]) : ov?.[en];
    return v && String(v).trim() ? v : def;
  };
  const headingDefault = `${t.hero.line1}\n${t.hero.line2}\n${t.hero.line3}`;
  const headingLines = L("heading_en", "heading_hi", headingDefault).split("\n");
  const subtitle = L("subtitle_en", "subtitle_hi", a.title2);
  const paragraph = L("paragraph_en", "paragraph_hi", a.paragraph);
  const storyTitle = L("story_title_en", "story_title_hi", a.storyTitle);
  const storyDefault = a.storyParas.join("\n");
  const storyParas = L("story_en", "story_hi", storyDefault).split("\n").filter((p) => p.trim());
  const mission = L("mission_en", "mission_hi", a.mission);
  const vision = L("vision_en", "vision_hi", a.vision);
  const img = (ov.image && ov.image.trim()) ? resolveImg(ov.image) : aboutImage;

  return (
    <div className="bg-white">
      <Helmet>
        <title>About Us | Superhost Cloud</title>
        <meta name="description" content="Superhost Cloud is a hospitality technology company helping Indian hotel owners grow bookings." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-36 pb-16 lg:pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4F6FA] to-white" />
        <div className="absolute top-10 left-10 w-80 h-80 rounded-full bg-[#E84B1C]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-3xl">
            <span className="font-head text-[#E84B1C] font-semibold tracking-wide uppercase text-sm">{a.eyebrow}</span>
            <h1 className="mt-3 font-head font-extrabold leading-[1.05] tracking-tight text-balance">
              {headingLines.map((ln, i) => (
                <span key={i} className="block text-[#12284B] text-3xl sm:text-4xl lg:text-5xl">{ln}</span>
              ))}
            </h1>
            <div className="mt-5 h-1.5 w-16 bg-[#E84B1C] rounded-full" />
            <p className="mt-5 font-head font-bold text-[#E84B1C] text-lg sm:text-xl lg:text-2xl leading-snug max-w-2xl text-balance">
              {subtitle}
            </p>
            <p className="mt-5 text-black/70 text-lg max-w-2xl">{paragraph}</p>
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img
              src={img}
              alt="Hotel exterior"
              className="rounded-2xl w-full h-[420px] object-cover shadow-2xl shadow-[#12284B]/20"
            />
          </Reveal>
          <Reveal delay={0.15}>
            <span className="font-head text-[#E84B1C] font-semibold tracking-wide uppercase text-sm">{a.storyEyebrow}</span>
            <h2 className="mt-3 font-head text-3xl md:text-4xl font-bold text-[#12284B] leading-tight">{storyTitle}</h2>
            <div className="mt-5 space-y-4 text-black/70 leading-relaxed">
              {storyParas.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-16 lg:py-20 bg-[#F4F6FA] border-y border-[#12284B]/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full bg-[#17205C] border border-white/10 rounded-2xl p-8 shadow-sm">
              <div className="h-14 w-14 rounded-xl bg-[#E84B1C]/20 flex items-center justify-center">
                <Target size={26} className="text-[#E84B1C]" />
              </div>
              <h3 className="mt-5 font-head text-2xl font-bold text-white">{a.missionTitle}</h3>
              <p className="mt-3 text-white/70 leading-relaxed">{mission}</p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full bg-[#17205C] border border-white/10 rounded-2xl p-8 shadow-sm">
              <div className="h-14 w-14 rounded-xl bg-[#E84B1C]/20 flex items-center justify-center">
                <Eye size={26} className="text-[#E84B1C]" />
              </div>
              <h3 className="mt-5 font-head text-2xl font-bold text-white">{a.visionTitle}</h3>
              <p className="mt-3 text-white/70 leading-relaxed">{vision}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {statsData.map((s) => (
              <StaggerItem key={s.id} className="text-center">
                <div className="font-head text-4xl lg:text-5xl font-extrabold text-[#17205C]">{s.value}</div>
                <div className="mt-2 text-black/60 text-sm lg:text-base">{t.stats[s.id]}</div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Why trust us */}
      <section className="py-16 lg:py-24 bg-[#F4F6FA] border-y border-[#12284B]/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <h2 className="font-head text-3xl md:text-5xl font-bold text-[#12284B] leading-tight">{a.trustTitle}</h2>
          </Reveal>
          <Stagger className="mt-14 grid md:grid-cols-3 gap-6">
            {a.values.map((v, i) => {
              const Icon = valueIcons[i] || ShieldCheck;
              return (
                <StaggerItem key={v.h}>
                  <div className="h-full bg-[#17205C] border border-white/10 rounded-2xl p-8 shadow-sm hover:border-[#E84B1C]/40 transition-colors duration-300">
                    <div className="h-14 w-14 rounded-xl bg-[#E84B1C]/20 flex items-center justify-center">
                      <Icon size={26} className="text-[#E84B1C]" />
                    </div>
                    <h3 className="mt-5 font-head text-xl font-semibold text-white">{v.h}</h3>
                    <p className="mt-2.5 text-white/70 leading-relaxed">{v.p}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      <CTASection title={t.cta.aboutTitle} subtitle={t.cta.aboutSubtitle} />
    </div>
  );
}
